export const MinutesPassed = (date) => {
    const date1 = new Date();
    const date2 = new Date(date);
    const diffTime = date2 - date1;
    return diffTime / (1000 * 60);
}

export const GetDateString = (date) => {
    let d = null;
    d = date ? new Date(date) : new Date();

    let month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear(),
        hours = d.getHours(),
        mins = d.getMinutes();
    if (month >= 1 & month <= 9)
        month = '0' + month.toString();
    if (day >= 0 & day <= 9)
        day = '0' + day.toString();
    if (hours >= 0 & hours <= 9)
        hours = '0' + hours.toString();
    if (mins >= 0 & mins <= 9)
        mins = '0' + mins.toString();

    return [year, month, day].join('-') + 'T' + [hours, mins].join(':');
}

/**
 * Normalise strictement une date en chaîne "YYYY-MM-DD" selon un fuseau horaire donné (par défaut Europe/Paris).
 * Immunisé contre les décalages de fuseau horaire du serveur ou du navigateur.
 */
export const getParisDateString = (dateInput, tz = 'Europe/Paris') => {
    if (!dateInput) return null;
    if (typeof dateInput === 'string') {
        const raw = dateInput.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            return raw;
        }
        const matchNoTz = raw.match(/^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(?::\d{2})?$/);
        if (matchNoTz) {
            return matchNoTz[1];
        }
    }

    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;

    try {
        const targetTz = (tz && typeof tz === 'string' && tz.trim()) ? tz.trim() : 'Europe/Paris';
        const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: targetTz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
        return formatter.format(d);
    } catch (e) {
        return d.toISOString().split('T')[0];
    }
};

export const getLocalDateString = getParisDateString;

/**
 * Récupère le jour de la semaine (0 = Dimanche, 1 = Lundi, ... 6 = Samedi) selon un fuseau donné.
 */
export const getParisDayOfWeek = (dateInput, tz = 'Europe/Paris') => {
    if (!dateInput) return null;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;

    try {
        const targetTz = (tz && typeof tz === 'string' && tz.trim()) ? tz.trim() : 'Europe/Paris';
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: targetTz,
            weekday: 'short'
        });
        const weekdayStr = formatter.format(d);
        const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
        return map[weekdayStr] !== undefined ? map[weekdayStr] : d.getDay();
    } catch (e) {
        return d.getDay();
    }
};

export const getLocalDayOfWeek = getParisDayOfWeek;

/**
 * Extrait de manière centralisée les dates d'aller et de retour d'un payload.
 */
export const getBookingDates = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return { outbound: null, returnTripDate: null };
    }
    const outbound = payload.tripdate || payload.tripDate || payload.scheduledAt || payload.pickupDate || null;

    let returnTripDate = null;
    if (payload.returnTrip && typeof payload.returnTrip === 'object') {
        returnTripDate = payload.returnTrip.tripdate || payload.returnTrip.tripDate || payload.returnTrip.scheduledAt || null;
    }
    if (!returnTripDate) {
        returnTripDate = payload.returnDate || payload.returnScheduledAt || null;
    }
    return { outbound, returnTripDate };
};

/**
 * Vérifie si une date précise est disponible selon les paramètres (Vacances, Dates bloquées, Jours de repos).
 */
export const checkDateAvailability = (dateInput, settings, customTz = null) => {
    if (!dateInput || !settings) return { available: true };

    const tz = customTz || settings?.timezone || settings?.timeZone || 'Europe/Paris';
    const dateStr = getParisDateString(dateInput, tz);
    if (!dateStr) return { available: true };

    // 1. Priorité 1 : Mode Vacances
    const vm = settings.vacation_mode;
    if (vm && vm.enabled && vm.startDate && vm.endDate) {
        if (dateStr >= vm.startDate && dateStr <= vm.endDate) {
            return {
                available: false,
                reason: 'vacation',
                message: vm.message || `Réservations suspendues pour congés annuels du ${vm.startDate} au ${vm.endDate}.`,
                date: dateStr,
                vacationStart: vm.startDate,
                vacationEnd: vm.endDate
            };
        }
    }

    // 2. Priorité 2 : Dates bloquées ponctuelles
    const blockedDates = Array.isArray(settings.blocked_dates) ? settings.blocked_dates : [];
    if (blockedDates.includes(dateStr)) {
        return {
            available: false,
            reason: 'blocked_date',
            message: `La date du ${dateStr} n'est pas disponible pour les réservations.`,
            date: dateStr
        };
    }

    // 3. Priorité 3 : Jours de repos récurrents
    const dayOfWeek = getParisDayOfWeek(dateInput, tz);
    const blockedWeekdays = Array.isArray(settings.blocked_weekdays) ? settings.blocked_weekdays : [];
    if (dayOfWeek !== null && blockedWeekdays.includes(dayOfWeek)) {
        const daysMapFr = {
            0: "dimanche",
            1: "lundi",
            2: "mardi",
            3: "mercredi",
            4: "jeudi",
            5: "vendredi",
            6: "samedi"
        };
        const dayLabel = daysMapFr[dayOfWeek] || "ce jour";
        return {
            available: false,
            reason: 'blocked_weekday',
            message: `Service indisponible le ${dayLabel}. Merci de sélectionner une autre date.`,
            date: dateStr,
            weekday: dayOfWeek
        };
    }

    return { available: true };
};

/**
 * Vérifie la disponibilité globale de l'aller ET du retour pour une demande de réservation/estimation.
 */
export const checkTripAvailability = (payload, settings, customTz = null) => {
    if (!payload || !settings) return { available: true };

    const tz = customTz || settings?.timezone || settings?.timeZone || payload?.timezone || null;
    const { outbound, returnTripDate } = getBookingDates(payload);

    if (outbound) {
        const outboundCheck = checkDateAvailability(outbound, settings, tz);
        if (!outboundCheck.available) {
            return {
                available: false,
                tripLeg: 'outbound',
                ...outboundCheck
            };
        }
    }

    if (returnTripDate) {
        const returnCheck = checkDateAvailability(returnTripDate, settings, tz);
        if (!returnCheck.available) {
            return {
                available: false,
                tripLeg: 'return',
                ...returnCheck
            };
        }
    }

    return { available: true };
};

/**
 * Récupère le préavis minimum configuré en minutes (paramètre admin settings.min_booking_time).
 * Défaut : 30 minutes si non configuré.
 */
export const getMinBookingLeadMinutes = (settings) => {
    const raw = settings?.min_booking_time !== undefined && settings?.min_booking_time !== null && settings?.min_booking_time !== ''
        ? settings.min_booking_time
        : settings?.min_booking_lead_minutes;
    if (raw !== undefined && raw !== null && raw !== '') {
        const parsed = Number(raw);
        if (!isNaN(parsed) && parsed >= 0) return parsed;
    }
    return 30;
};

/**
 * Calcule un instant futur (now + leadMinutes + marginMinutes) et l'arrondit au multiple
 * de 5 minutes SUPÉRIEUR (secondes et millisecondes mises à zéro).
 * Exemples :
 * - 10:00:10 avec 60 min + 5 min marge (11:05:10) -> arrondi supérieur = 11:10:00
 * - 10:00:00 pile avec 60 min + 5 min marge (11:05:00) -> 11:05:00
 * - 10:04:59 avec 60 min + 5 min marge (11:09:59) -> 11:10:00
 */
/**
 * Arrondit strictement un timestamp ou un Date au multiple supérieur de 5 minutes.
 * - 10:00:00 -> 10:00:00
 * - 10:00:01 -> 10:05:00
 * - 10:04:59 -> 10:05:00
 */
export const roundUpToNextFiveMinutes = (timeInput) => {
    const ms = timeInput instanceof Date
        ? timeInput.getTime()
        : (typeof timeInput === 'number' && !isNaN(timeInput) ? timeInput : (timeInput ? new Date(timeInput).getTime() : Date.now()));
    const safeMs = isNaN(ms) ? Date.now() : ms;
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    return new Date(Math.ceil(safeMs / FIVE_MINUTES_MS) * FIVE_MINUTES_MS);
};

export const computeMinBookingDate = (nowInput, minLeadMinutes = 30, marginMinutes = 0) => {
    const nowMs = nowInput instanceof Date
        ? nowInput.getTime()
        : (typeof nowInput === 'number' && !isNaN(nowInput)
            ? nowInput
            : (nowInput ? new Date(nowInput).getTime() : Date.now()));
    const safeNowMs = isNaN(nowMs) ? Date.now() : nowMs;
    const targetMs = safeNowMs + (Number(minLeadMinutes || 0) + Number(marginMinutes || 0)) * 60 * 1000;
    return roundUpToNextFiveMinutes(targetMs);
};

/**
 * Heure proposée par défaut pour une réservation :
 * (maintenant + préavis admin + marge de 5 minutes), arrondie au multiple de 5 minutes supérieur.
 */
export const computeDefaultBookingDate = (nowInput, settings) => {
    const minLead = getMinBookingLeadMinutes(settings);
    return computeMinBookingDate(nowInput, minLead, 5);
};

/**
 * Borne inférieure stricte sélectionnable dans le sélecteur (minimumDate) :
 * (maintenant + préavis admin), arrondie au multiple de 5 minutes supérieur.
 * Toutes les heures/minutes antérieures à cette borne doivent être grisées.
 */
export const computePickerMinimumDate = (nowInput, settings) => {
    const minLead = getMinBookingLeadMinutes(settings);
    return computeMinBookingDate(nowInput, minLead, 0);
};

/**
 * Valide si une date choisie respecte le préavis minimum requis par rapport à un instant de référence.
 */
export const isBookingTimeValid = (selectedDate, settings, referenceTimeMs = Date.now()) => {
    if (!selectedDate) return false;
    const selectedMs = selectedDate instanceof Date ? selectedDate.getTime() : new Date(selectedDate).getTime();
    if (isNaN(selectedMs)) return false;
    const minAllowedMs = computePickerMinimumDate(referenceTimeMs, settings).getTime();
    return selectedMs >= minAllowedMs;
};

/**
 * Formate un message d'erreur clair et traduit pour les codes INVALID_DATE ou INVALID_DATE:XX.
 */
export const formatInvalidDateMessage = (rawError, settings, t) => {
    const str = String(rawError || '');
    const match = str.match(/INVALID_DATE(?::(\d+))?/i);
    const minutes = match && match[1] ? Number(match[1]) : getMinBookingLeadMinutes(settings);
    if (typeof t === 'function') {
        return t('error_invalid_date_min_notice', {
            minutes,
            defaultValue: `Réservation possible au minimum ${minutes} minutes à l'avance.`
        });
    }
    return `Réservation possible au minimum ${minutes} minutes à l'avance.`;
};

/**
 * Calcule la date/heure minimale autorisée pour un trajet retour.
 * Règle métier :
 * minRetour = max(
 *   date minimale liée au préavis admin à l'instant de référence (computePickerMinimumDate(now, settings)),
 *   date aller + durée estimée du trajet (en secondes) + marge minimale (30 minutes par défaut)
 * )
 * Le résultat est systématiquement arrondi au multiple supérieur de 5 minutes.
 *
 * @param {Date|number|string} outboundDate - Date de l'aller
 * @param {number}             [durationSec=0] - Durée estimée du trajet en secondes
 * @param {object}             [settings={}] - Paramètres admin (min_booking_lead_minutes)
 * @param {Date|number}        [now=Date.now()] - Instant de référence pour le préavis
 * @param {number}             [marginMinutes=30] - Marge minimale en minutes
 * @returns {Date} Date minimale pour le retour
 */
export const computeMinReturnDate = (
    outboundDate,
    durationSec = 0,
    settings = {},
    now = Date.now(),
    marginMinutes = 30
) => {
    const minLeadDate = computePickerMinimumDate(now, settings);
    if (!outboundDate) return minLeadDate;

    const outboundMs = outboundDate instanceof Date
        ? outboundDate.getTime()
        : (typeof outboundDate === 'number' ? outboundDate : new Date(outboundDate).getTime());

    if (isNaN(outboundMs)) return minLeadDate;

    const validDurationSec = (typeof durationSec === 'number' && !isNaN(durationSec) && durationSec > 0)
        ? durationSec
        : 0;

    const minReturnFromOutboundMs = outboundMs + (validDurationSec * 1000) + (marginMinutes * 60 * 1000);
    const targetMs = Math.max(minLeadDate.getTime(), minReturnFromOutboundMs);
    return roundUpToNextFiveMinutes(targetMs);
};

// Fuseaux horaires pour la règle déterministe d'heure d'été européenne (Directive 2000/84/CE)
const CET_ZONES = new Set([
    'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome',
    'Europe/Brussels', 'Europe/Amsterdam', 'Europe/Zurich'
]);
const WET_ZONES = new Set([
    'Europe/London', 'Europe/Dublin', 'Europe/Lisbon'
]);
const EET_ZONES = new Set([
    'Europe/Athens', 'Europe/Bucharest', 'Europe/Helsinki'
]);

const getLastSundayUtc = (year, month, hourUtc = 1) => {
    const d = new Date(Date.UTC(year, month - 1, 31, hourUtc, 0, 0, 0));
    const dayOfWeek = d.getUTCDay();
    d.setUTCDate(31 - dayOfWeek);
    return d.getTime();
};

export const isEuropeanDst = (utcMillis) => {
    const d = new Date(utcMillis);
    const year = d.getUTCFullYear();
    const marchTransition = getLastSundayUtc(year, 3, 1);
    const octoberTransition = getLastSundayUtc(year, 10, 1);
    return utcMillis >= marchTransition && utcMillis < octoberTransition;
};

export const getEuFallbackOffsetMinutes = (utcMillis, tz) => {
    const dst = isEuropeanDst(utcMillis);
    if (CET_ZONES.has(tz)) return dst ? 120 : 60;
    if (WET_ZONES.has(tz)) return dst ? 60 : 0;
    if (EET_ZONES.has(tz)) return dst ? 180 : 120;
    return null;
};

// [TEMP_HERMES_INTL_CHECK] Log temporaire pour vérifier le support Intl + timeZone sur build release Hermes Android. À retirer après validation sur appareil.
let _hermesIntlChecked = false;
const checkHermesIntlSupportOnce = (tz) => {
    if (_hermesIntlChecked) return;
    _hermesIntlChecked = true;
    try {
        const fmt = new Intl.DateTimeFormat('en-u-ca-iso8601', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
        const resolved = fmt.resolvedOptions ? fmt.resolvedOptions() : {};
        console.log('[TEMP_HERMES_INTL_CHECK] Intl.DateTimeFormat SUCCESS:', {
            requestedTz: tz,
            resolvedTz: resolved.timeZone,
            locale: resolved.locale,
            calendar: resolved.calendar
        });
    } catch (e) {
        console.warn('[TEMP_HERMES_INTL_CHECK] Intl.DateTimeFormat FAILED:', e?.message || e);
    }
};

/**
 * Formate une Date en chaîne lisible (date + heure) dans le fuseau horaire métier (settings.timezone).
 * Le fuseau du téléphone ne doit JAMAIS servir de repli : le repli strict est le fuseau
 * de l'instance (Europe/Paris par défaut).
 *
 * Retourne { dateStr, timeStr } (ex: { dateStr: "12 oct.", timeStr: "14:30" })
 *
 * @param {Date|number|string} date     - L'instant à formater
 * @param {object}             settings - Paramètres admin (settings.timezone ou settings.timeZone)
 * @param {string}             [locale] - Locale ("fr" par défaut)
 * @returns {{ dateStr: string, timeStr: string }}
 */
export const formatDateInBusinessTz = (date, settings, locale = 'fr') => {
    const rawTz = settings?.timezone || settings?.timeZone;
    const tz = (typeof rawTz === 'string' && rawTz.trim().length > 0) ? rawTz.trim() : 'Europe/Paris';

    // Diagnostic temporaire pour Hermes Android
    checkHermesIntlSupportOnce(tz);

    const MONTH_NAMES_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
                            'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
    const MONTH_NAMES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const MONTH_NAMES_FULL_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                                 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const MONTH_NAMES_FULL_EN = ['January', 'February', 'March', 'April', 'May', 'June',
                                 'July', 'August', 'September', 'October', 'November', 'December'];
    const isEn = typeof locale === 'string' && locale.toLowerCase().startsWith('en');

    const utcMs = date instanceof Date
        ? date.getTime()
        : (typeof date === 'number' ? date : new Date(date).getTime());

    try {
        const formatter = new Intl.DateTimeFormat('en-u-ca-iso8601', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        const parts = {};
        formatter.formatToParts(new Date(utcMs)).forEach(p => { parts[p.type] = p.value; });

        const D = parseInt(parts.day, 10);
        const M = parseInt(parts.month, 10);
        const Y = parseInt(parts.year, 10);
        const H = String(parts.hour === '24' ? '00' : parts.hour).padStart(2, '0');
        const m = String(parts.minute).padStart(2, '0');

        const monthNames = isEn ? MONTH_NAMES_EN : MONTH_NAMES_FR;
        const monthLabel = monthNames[M - 1] || String(M);
        const monthFullNames = isEn ? MONTH_NAMES_FULL_EN : MONTH_NAMES_FULL_FR;
        const monthFullLabel = monthFullNames[M - 1] || String(M);

        const dateStr = isEn ? `${monthLabel} ${D}` : `${D} ${monthLabel}`;
        const fullDateStr = isEn ? `${monthFullLabel} ${D}, ${Y}` : `${D} ${monthFullLabel} ${Y}`;
        const timeStr = `${H}:${m}`;

        return { dateStr, timeStr, fullDateStr };
    } catch (_) {
        // En cas d'exception Intl sur le fuseau (ex: environnement Hermes incomplet) :
        // 1. Règle déterministe pour les zones européennes (CET, WET, EET)
        const euOffset = getEuFallbackOffsetMinutes(utcMs, tz);
        if (euOffset !== null) {
            const localMs = utcMs + euOffset * 60000;
            const ld = new Date(localMs);
            const D = ld.getUTCDate();
            const M = ld.getUTCMonth() + 1;
            const Y = ld.getUTCFullYear();
            const H = String(ld.getUTCHours()).padStart(2, '0');
            const m = String(ld.getUTCMinutes()).padStart(2, '0');
            const monthNames = isEn ? MONTH_NAMES_EN : MONTH_NAMES_FR;
            const monthLabel = monthNames[M - 1] || String(M);
            const monthFullNames = isEn ? MONTH_NAMES_FULL_EN : MONTH_NAMES_FULL_FR;
            const monthFullLabel = monthFullNames[M - 1] || String(M);
            return {
                dateStr: isEn ? `${monthLabel} ${D}` : `${D} ${monthLabel}`,
                timeStr: `${H}:${m}`,
                fullDateStr: isEn ? `${monthFullLabel} ${D}, ${Y}` : `${D} ${monthFullLabel} ${Y}`
            };
        }

        // 2. Pour les autres fuseaux, repli explicite sur l'heure UTC avec suffixe " UTC" et avertissement console
        console.warn(`[formatDateInBusinessTz] Intl non supporté pour le fuseau "${tz}". Repli explicite sur UTC avec suffixe UTC.`);
        const d = new Date(utcMs);
        const D = d.getUTCDate();
        const M = d.getUTCMonth() + 1;
        const Y = d.getUTCFullYear();
        const H = String(d.getUTCHours()).padStart(2, '0');
        const m = String(d.getUTCMinutes()).padStart(2, '0');
        const monthNames = isEn ? MONTH_NAMES_EN : MONTH_NAMES_FR;
        const monthLabel = monthNames[M - 1] || String(M);
        const monthFullNames = isEn ? MONTH_NAMES_FULL_EN : MONTH_NAMES_FULL_FR;
        const monthFullLabel = monthFullNames[M - 1] || String(M);
        return {
            dateStr: isEn ? `${monthLabel} ${D}` : `${D} ${monthLabel}`,
            timeStr: `${H}:${m} UTC`,
            fullDateStr: isEn ? `${monthFullLabel} ${D}, ${Y}` : `${D} ${monthFullLabel} ${Y}`
        };
    }
};